import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Alert, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import { TrendingUp, CheckCircle, Schedule, Refresh, Warning, Block } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';


interface Statistics {
  totalActiveClients: number;
  totalPendingFilings: number;
  totalOverdueFilings: number;
  totalWorkInProgressFilings: number;
  totalReceivedFilings: number;
  totalFiledFilings: number;
}

interface EmployeeStat {
  name: string;
  email: string;
  totalClients: number;
  pendingFilings: number;
  overdueFilings: number;
  workInProgressFilings: number;
  receivedFilings: number;
  totalFiled: number;
  inactiveClients: number;
}

interface DashboardData {
  statistics: Statistics;
  employeeStats: EmployeeStat[];
  inactiveClients: number;
}

const SuperAdminDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
        const response = await api.get('/dashboard/super-admin');
      console.log('Dashboard data:', response.data);
      setData(response.data.data);
    } catch (error: any) {
      console.error('Error fetching dashboard:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

    const handleCardClick = (filterType: string) => {
    // Navigate to filings page with filter
    navigate(`/filings?status=${filterType}`);
    };

    const handleEmployeeClick = (employeeName: string) => {
    // Navigate to filings page filtered by employee (ALL statuses)
    navigate(`/filings?employee=${employeeName}`);
    };
    const handleEmployeeStatClick = (employeeName: string, status: string) => {
    console.log('🔍 Navigating with:', { employeeName, status });
    
    // Navigate based on status type
    if (status === 'all') {
        // Total Clients - show all clients for this employee
        navigate(`/clients?employee=${employeeName}`);
    } else if (status === 'INACTIVE') {
        // Inactive Clients - show inactive clients for this employee
        navigate(`/clients?employee=${employeeName}&status=INACTIVE`);
    } else {
        // Filing statuses - show filings filtered by employee AND status
        const url = `/filings?employee=${encodeURIComponent(employeeName)}&status=${status}`;
        console.log('🔍 Navigating to:', url);
        navigate(url);
    }
    };
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!data) return null;

  const totalPending = data.employeeStats.reduce((sum, emp) => sum + emp.pendingFilings, 0);
  const totalClients = data.employeeStats.reduce((sum, emp) => sum + emp.totalClients, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '2rem', mb: 1, color: '#1a1a1a' }}>
            Super Admin Dashboard
          </Typography>
          <Typography sx={{ color: '#666666', fontSize: '1rem' }}>
            Team performance and upcoming deadlines overview
          </Typography>
        </Box>
        <IconButton 
          onClick={fetchDashboard}
          sx={{ 
            color: '#667eea',
            border: '2px solid #667eea',
            borderRadius: 2,
            p: 1.5,
            '&:hover': { 
              backgroundColor: '#667eea',
              color: '#FFFFFF'
            },
            transition: 'all 0.2s'
          }}
        >
          <Refresh />
        </IconButton>
      </Box>

{/* Summary Cards - CLICKABLE */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        {/* Total Active Clients Card */}
        <Card 
          sx={{ 
            border: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#FFFFFF',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
              borderRadius: '50%',
              transform: 'translate(50%, -50%)'
            }
          }} 
          elevation={0}
        >
          <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TrendingUp sx={{ fontSize: 28 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Active Clients
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ fontWeight: 700, fontSize: '3rem', lineHeight: 1 }}>
              {data.statistics.totalActiveClients}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              total active
            </Typography>
          </CardContent>
        </Card>

        {/* Total Employees Card */}
        <Card 
          sx={{ 
            border: 'none',
            background: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }} 
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TrendingUp sx={{ fontSize: 28, color: '#667eea' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 1, color: '#6B7280' }}>
                Active Employees
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ fontWeight: 700, fontSize: '3rem', lineHeight: 1, color: '#1a1a1a' }}>
              {data.employeeStats.length}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#6B7280' }}>
              team members
            </Typography>
          </CardContent>
        </Card>

        {/* Total Pending Card - CLICKABLE */}
        <Card 
          onClick={() => handleCardClick('PENDING')}
          sx={{ 
            border: 'none',
            background: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
            },
          }} 
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Warning sx={{ fontSize: 28, color: '#F59E0B' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 1, color: '#6B7280' }}>
                Total Pending
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ fontWeight: 700, fontSize: '3rem', lineHeight: 1, color: '#1a1a1a' }}>
              {totalPending}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#6B7280' }}>
              across all employees
            </Typography>
          </CardContent>
        </Card>

        {/* Inactive Clients Card - CLICKABLE */}
        <Card 
          onClick={() => handleCardClick('INACTIVE')}
          sx={{ 
            border: 'none',
            background: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            },
          }} 
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Block sx={{ fontSize: 28, color: '#3B82F6' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 1, color: '#6B7280' }}>
                Inactive Clients
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ fontWeight: 700, fontSize: '3rem', lineHeight: 1, color: '#1a1a1a' }}>
              {data.inactiveClients}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#6B7280' }}>
              paused services
            </Typography>
          </CardContent>
        </Card>
      </Box>

{/* Statistics Overview - WITH CHARTS */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
        {/* Bar Chart - Filing Status Distribution */}
        <Paper sx={{ p: 4, border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TrendingUp sx={{ color: '#FFFFFF', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
              Filing Status Overview
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { name: 'Pending', value: data.statistics.totalPendingFilings, fill: '#F59E0B' },
              { name: 'Overdue', value: data.statistics.totalOverdueFilings, fill: '#EF4444' },
              { name: 'Work In Progress', value: data.statistics.totalWorkInProgressFilings, fill: '#8B5CF6' },
              { name: 'Received', value: data.statistics.totalReceivedFilings, fill: '#3B82F6' },
              { name: 'Filed', value: data.statistics.totalFiledFilings, fill: '#10B981' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" stroke="#6B7280" style={{ fontSize: '0.875rem', fontWeight: 600 }} />
              <YAxis stroke="#6B7280" style={{ fontSize: '0.875rem' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E5E7EB', 
                  borderRadius: 8,
                  fontSize: '0.875rem',
                  fontWeight: 600 
                }} 
              />
          <Bar dataKey="value" radius={[8, 8, 0, 0]} onClick={(data) => data.name && handleCardClick(data.name.toUpperCase())} style={{ cursor: 'pointer' }} />            </BarChart>
          </ResponsiveContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#F59E0B' }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6B7280' }}>Pending ({data.statistics.totalPendingFilings})</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#EF4444' }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6B7280' }}>Overdue ({data.statistics.totalOverdueFilings})</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#8B5CF6' }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6B7280' }}>Work In Progress ({data.statistics.totalWorkInProgressFilings})</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#3B82F6' }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6B7280' }}>Received ({data.statistics.totalReceivedFilings})</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#10B981' }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6B7280' }}>Filed ({data.statistics.totalFiledFilings})</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Pie Chart - Status Distribution */}
        {/* Pie Chart - Status Distribution */}
        <Paper sx={{ p: 4, border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircle sx={{ color: '#FFFFFF', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
              Status Distribution
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={(() => {
                  const stats = [
                    { name: 'Pending', value: data.statistics.totalPendingFilings, fill: '#F59E0B' },
                    { name: 'Overdue', value: data.statistics.totalOverdueFilings, fill: '#EF4444' },
                    { name: 'Work In Progress', value: data.statistics.totalWorkInProgressFilings, fill: '#8B5CF6' },
                    { name: 'Received', value: data.statistics.totalReceivedFilings, fill: '#3B82F6' },
                    { name: 'Filed', value: data.statistics.totalFiledFilings, fill: '#10B981' },
                  ];
                  const total = stats.reduce((sum, item) => sum + item.value, 0);
                  return stats.map(item => ({
                    ...item,
                    displayValue: item.value || 0.1, // Show tiny slice for 0 values
                    actualValue: item.value,
                    percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0'
                  }));
                })()}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={false}
                outerRadius={90}
                dataKey="displayValue"
                style={{ cursor: 'pointer' }}
                onClick={(data) => data.name && handleCardClick(data.name.toUpperCase())}
              >
                {(() => {
                  const stats = [
                    { name: 'Pending', value: data.statistics.totalPendingFilings, fill: '#F59E0B' },
                    { name: 'Overdue', value: data.statistics.totalOverdueFilings, fill: '#EF4444' },
                    { name: 'Work In Progress', value: data.statistics.totalWorkInProgressFilings, fill: '#8B5CF6' },
                    { name: 'Received', value: data.statistics.totalReceivedFilings, fill: '#3B82F6' },
                    { name: 'Filed', value: data.statistics.totalFiledFilings, fill: '#10B981' },
                  ];
                  return stats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ));
                })()}
              </Pie>
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <Box
                        sx={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: 2,
                          p: 2,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 0.5, color: '#111827' }}>
                          {data.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                          Count: <strong>{data.actualValue}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                          Percentage: <strong>{data.percentage}%</strong>
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Simple Legend - No Percentages */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#F59E0B' }} />
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                Pending: <strong>{data.statistics.totalPendingFilings}</strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#EF4444' }} />
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                Overdue: <strong>{data.statistics.totalOverdueFilings}</strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#8B5CF6' }} />
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                WIP: <strong>{data.statistics.totalWorkInProgressFilings}</strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#3B82F6' }} />
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                Received: <strong>{data.statistics.totalReceivedFilings}</strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#10B981' }} />
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                Filed: <strong>{data.statistics.totalFiledFilings}</strong>
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body2" sx={{ textAlign: 'center', color: '#6B7280', fontSize: '0.875rem', mt: 2, pt: 2, borderTop: '1px solid #E5E7EB' }}>
            Total Active Clients: <strong>{data.statistics.totalActiveClients}</strong>
          </Typography>
        </Paper>

        {/* Employee Performance Comparison Bar Chart */}
        <Paper sx={{ p: 4, border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', gridColumn: { xs: '1', lg: 'span 2' } }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TrendingUp sx={{ color: '#FFFFFF', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
              Employee Performance Comparison
            </Typography>
          </Box>
            <ResponsiveContainer width="100%" height={350}>
            <BarChart data={data.employeeStats.map(emp => ({
              name: emp.name.split(' ')[0], // Use first name only for cleaner display
              Total: emp.totalClients,
              Pending: emp.pendingFilings,
              Overdue: emp.overdueFilings,
              'Work In Progress': emp.workInProgressFilings,
              Received: emp.receivedFilings,
              Filed: emp.totalFiled,
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" stroke="#6B7280" style={{ fontSize: '0.875rem', fontWeight: 600 }} />
              <YAxis stroke="#6B7280" style={{ fontSize: '0.875rem' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E5E7EB', 
                  borderRadius: 8,
                  fontSize: '0.875rem',
                  fontWeight: 600 
                }} 
              />
              <Legend wrapperStyle={{ fontSize: '0.875rem', fontWeight: 600 }} />
              <Bar dataKey="Total" fill="#667eea" radius={[4, 4, 0, 0]} name="Total Clients" />
              <Bar dataKey="Pending" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Pending" />
              <Bar dataKey="Overdue" fill="#EF4444" radius={[4, 4, 0, 0]} name="Overdue" />
              <Bar dataKey="Work In Progress" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Work In Progress" />
              <Bar dataKey="Received" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Received" />
              <Bar dataKey="Filed" fill="#10B981" radius={[4, 4, 0, 0]} name="Filed" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>


      {/* Employee Performance - CLICKABLE */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            width: 40, 
            height: 40, 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <TrendingUp sx={{ color: '#FFFFFF', fontSize: 24 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
            Employee Performance
          </Typography>
        </Box>

        {data.employeeStats.length === 0 ? (
          <Alert severity="info">No employees found</Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            {data.employeeStats.map((employee) => (
              <Card 
                key={employee.email}
                onClick={() => handleEmployeeClick(employee.name)}
                sx={{ 
                  border: '1px solid #E5E7EB', 
                  height: '100%', 
                  borderRadius: 3, 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
                  transition: 'all 0.3s', 
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)', 
                    transform: 'translateY(-4px)',
                    borderColor: '#667eea',
                  } 
                }} 
                elevation={0}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pb: 3, borderBottom: '1px solid #F3F4F6' }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                      }}
                    >
                      {employee.name.charAt(0).toUpperCase()}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', mb: 0.5 }}>
                        {employee.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {employee.email}
                      </Typography>
                    </Box>
                  </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Total Clients - Shows ALL filings for this employee */}
                    <Box 
                        onClick={(e) => {
                        e.stopPropagation();
                        handleEmployeeStatClick(employee.name, 'all');
                        }}
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        backgroundColor: '#F9FAFB', 
                        borderRadius: 2, 
                        border: '1px solid #F3F4F6',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                            transform: 'translateX(4px)',
                        }
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, fontSize: '0.875rem' }}>
                        Total Clients
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea', fontSize: '1.5rem' }}>
                        {employee.totalClients}
                        </Typography>
                    </Box>
                    {/* Pending Filings - Shows only PENDING */}
                    <Box 
                        onClick={(e) => {
                        e.stopPropagation();
                        handleEmployeeStatClick(employee.name, 'PENDING');
                        }}
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        backgroundColor: '#FEF3C7', 
                        borderRadius: 2, 
                        border: '1px solid #FCD34D',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: '#FDE68A',
                            transform: 'translateX(4px)',
                        }
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 600, fontSize: '0.875rem' }}>
                        Pending Filings
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400E', fontSize: '1.5rem' }}>
                        {employee.pendingFilings}
                        </Typography>
                    </Box>

                    {/* Overdue Filings - Shows only OVERDUE */}
                    <Box 
                        onClick={(e) => {
                        e.stopPropagation();
                        handleEmployeeStatClick(employee.name, 'OVERDUE');
                        }}
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        backgroundColor: '#FEE2E2', 
                        borderRadius: 2, 
                        border: '1px solid #FCA5A5',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: '#FECACA',
                            transform: 'translateX(4px)',
                        }
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#991B1B', fontWeight: 600, fontSize: '0.875rem' }}>
                        Overdue Filings
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991B1B', fontSize: '1.5rem' }}>
                        {employee.overdueFilings}
                        </Typography>
                    </Box>

                    {/* Work In Progress Filings - Shows only WORK_IN_PROGRESS */}
                    <Box 
                        onClick={(e) => {
                        e.stopPropagation();
                        handleEmployeeStatClick(employee.name, 'WORK_IN_PROGRESS');
                        }}
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        backgroundColor: '#EDE9FE', 
                        borderRadius: 2, 
                        border: '1px solid #C4B5FD',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: '#DDD6FE',
                            transform: 'translateX(4px)',
                        }
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#5B21B6', fontWeight: 600, fontSize: '0.875rem' }}>
                        Work In Progress
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#5B21B6', fontSize: '1.5rem' }}>
                        {employee.workInProgressFilings}
                        </Typography>
                    </Box>

                    {/* Received Filings - Shows only RECEIVED */}
                    <Box 
                        onClick={(e) => {
                        e.stopPropagation();
                        handleEmployeeStatClick(employee.name, 'RECEIVED');
                        }}
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        backgroundColor: '#DBEAFE', 
                        borderRadius: 2, 
                        border: '1px solid #93C5FD',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: '#BFDBFE',
                            transform: 'translateX(4px)',
                        }
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#1E40AF', fontWeight: 600, fontSize: '0.875rem' }}>
                        Received Filings
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E40AF', fontSize: '1.5rem' }}>
                        {employee.receivedFilings}
                        </Typography>
                    </Box>

                    {/* Total Filed - Shows only FILED */}
                    <Box 
                        onClick={(e) => {
                        e.stopPropagation();
                        handleEmployeeStatClick(employee.name, 'FILED');
                        }}
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        backgroundColor: '#D1FAE5', 
                        borderRadius: 2, 
                        border: '1px solid #6EE7B7',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: '#A7F3D0',
                            transform: 'translateX(4px)',
                        }
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#065F46', fontWeight: 600, fontSize: '0.875rem' }}>
                        Total Filed
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#065F46', fontSize: '1.5rem' }}>
                        {employee.totalFiled}
                        </Typography>
                    </Box>

                    {/* Inactive Clients - Shows only INACTIVE */}
                    <Box 
                        onClick={(e) => {
                        e.stopPropagation();
                        handleEmployeeStatClick(employee.name, 'INACTIVE');
                        }}
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        backgroundColor: '#F3F4F6', 
                        borderRadius: 2, 
                        border: '1px solid #D1D5DB',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: '#BFDBFE',
                            transform: 'translateX(4px)',
                        }
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, fontSize: '0.875rem' }}>
                        Inactive Clients
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', fontSize: '1.5rem' }}>
                        {employee.inactiveClients}
                        </Typography>
                    </Box>
                    </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard;